import { useState, useCallback } from 'react';
import Stack from '@mui/material/Stack';
import { CustomField } from '@/components/shared/CustomField';
import { RiskFieldsTypeEnum, TemplateFieldTypeEnum } from '@/__generated__/graphql';
import { textFieldStyles } from '../styles';
import { RiskFactorInputTabs } from '@/components/shared/RiskFactorInputTabs';

type Props = {
  shipsAtBerth: string;
  onChange: ({
    pofValues,
    inputs,
  }: {
    pofValues?: { [key: string]: string };
    inputs?: {
      id: string;
      value: string;
    }[];
  }) => void;
};

export const FenderPanelFramingPoF = ({ shipsAtBerth, onChange }: Props) => {
  const [selectedValues, setSelectedValues] = useState<{ [key: string]: string | null }>({
    E: '',
    N: `${shipsAtBerth}`,
    D: '',
    F: '',
  });

  const changeField = (value: string, fieldId: string) => {
    setSelectedValues((current) => ({
      ...current,
      [fieldId]: value,
    }));
  };

  const changeRiskFactor = useCallback(
    (selectedRiskFactorValues: { [key: string]: string | null }) => {
      const inputValues = [
        { id: 'risk_base_pof_value', value: selectedValues.E ?? '' },
        { id: 'risk_ships_accepted_annually_at_berth', value: selectedValues.N ?? '' },
        { id: 'risk_design_factor', value: selectedValues.D ?? '' },
        { id: 'risk_experience_factor', value: selectedValues.F ?? '' },
        {
          id: 'risk_safety_value_pof_c',
          value: selectedRiskFactorValues.risk_safety_calculated_value_pof ?? '',
        },
        {
          id: 'risk_environment_value_pof_c',
          value: selectedRiskFactorValues.risk_environment_calculated_value_pof ?? '',
        },
        {
          id: 'risk_public_impact_value_pof_c',
          value: selectedRiskFactorValues.risk_public_impact_calculated_value_pof ?? '',
        },
        {
          id: 'risk_direct_cost_value_pof_c',
          value: selectedRiskFactorValues.risk_direct_cost_calculated_value_pof ?? '',
        },
        {
          id: 'risk_business_loss_value_pof_c',
          value: selectedRiskFactorValues.risk_business_loss_calculated_value_pof ?? '',
        },
      ];
      const pofValues: { [key: string]: string } = {
        risk_safety_calculated_value_pof:
          selectedRiskFactorValues.risk_safety_calculated_value_pof ?? '',
        risk_environment_calculated_value_pof:
          selectedRiskFactorValues.risk_environment_calculated_value_pof ?? '',
        risk_public_impact_calculated_value_pof:
          selectedRiskFactorValues.risk_public_impact_calculated_value_pof ?? '',
        risk_direct_cost_calculated_value_pof:
          selectedRiskFactorValues.risk_direct_cost_calculated_value_pof ?? '',
        risk_business_loss_calculated_value_pof:
          selectedRiskFactorValues.risk_business_loss_calculated_value_pof ?? '',
      };
      const filteredInputValues = inputValues.filter((input) => input.value !== '');
      onChange({ inputs: filteredInputValues, pofValues });
    },
    [onChange, selectedValues]
  );

  return (
    <Stack spacing={3}>
      <Stack direction="row" gap={3} justifyContent="space-between" width="100%">
        <Stack direction="row" gap={3} alignItems="flex-end" width="100%">
          <CustomField
            location={RiskFieldsTypeEnum.Pof.toString()}
            sx={textFieldStyles}
            fieldType={TemplateFieldTypeEnum.Input}
            fieldName="Base PoF value (E)"
            onChange={(value) => changeField(value, 'E')}
            commentIdentifier="risk_base_pof_value"
            placeholder="Enter value or will be taken from inspection"
            initialValue=""
          />
        </Stack>
        <Stack direction="row" gap={3} alignItems="flex-end" width="100%">
          <CustomField
            location={RiskFieldsTypeEnum.Pof.toString()}
            sx={textFieldStyles}
            fieldType={TemplateFieldTypeEnum.Input}
            fieldName="Ships accepted annually at berth (N)"
            onChange={(value) => changeField(value, 'N')}
            commentIdentifier="risk_ships_accepted_annually_at_berth"
            initialValue={shipsAtBerth}
          />
        </Stack>
      </Stack>
      <Stack direction="row" gap={3} justifyContent="space-between" width="100%">
        <Stack direction="row" gap={3} alignItems="flex-end" width="100%">
          <CustomField
            location={RiskFieldsTypeEnum.Pof.toString()}
            fieldType={TemplateFieldTypeEnum.SingleSelect}
            fieldName="Design factor (D)"
            fieldOptions={['1', '10']}
            onChange={(value) => changeField(value, 'D')}
            commentIdentifier="risk_design_factor"
            initialValue=""
          />
        </Stack>
        <Stack direction="row" gap={3} alignItems="flex-end" width="100%">
          <CustomField
            location={RiskFieldsTypeEnum.Pof.toString()}
            sx={textFieldStyles}
            fieldType={TemplateFieldTypeEnum.Input}
            fieldName="Experience factor (F)"
            onChange={(value) => changeField(value, 'F')}
            commentIdentifier="risk_experience_factor"
            initialValue=""
          />
        </Stack>
      </Stack>
      <RiskFactorInputTabs parentTab="pof" onInputChange={changeRiskFactor} />
    </Stack>
  );
};
